import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { toast } from "@/components/ui/sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AddressAutocomplete } from "@/components/ui/address-autocomplete";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useCharacteristics,
  useCompanySizes,
  useProfile,
  useSectors,
} from "@/features/questionnaire/api";
import { patchProfile } from "@/features/questionnaire/api";
import { enterpriseAddressPatch } from "@/features/questionnaire/lib/address";
import {
  groupByCategory,
  groupCharacteristics,
} from "@/features/questionnaire/lib/grouping";
import { setOnBehalfOf,
  setOnBehalfReadOnly } from "@/lib/api/on-behalf";

const ENTERPRISE_TYPES = [
  "self_employed",
  "private",
  "cooperative",
  "not_for_profit",
  "economical_development",
  "municipality",
  "other",
] as const;

import { useFormationGroupQuery } from "../api";
import { fieldErrors } from "../lib/errors";

/**
 * A participant's enterprise profile, corrected by an administrator.
 *
 * This is the one genuinely new capability in the ticket on the Django side;
 * on the API side `EnterpriseProfileViewSet` has carried it all along through
 * `?on_behalf_of`, so the SPA needed the screen rather than the endpoint.
 *
 * The fields are the ones the ticket names: sectors, characteristics, employee
 * count (`company_size`, a field on the profile — not a characteristic) and the
 * contact details. Validation is the server's: the same rules the participant's
 * own form meets, address re-validation included.
 */
export function ParticipantProfileForm({
  groupId,
  profileId,
}: {
  groupId: string;
  profileId: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // This screen writes, so it disarms the consultation-only flag rather than
  // relying on `ParticipantDataView`'s unmount cleanup having run first — the
  // administrator arrives here from that view's own link, and AC 14's save
  // should not depend on which effect React flushes first.
  setOnBehalfOf(profileId);
  setOnBehalfReadOnly(false);
  useEffect(() => {
    setOnBehalfOf(profileId);
    setOnBehalfReadOnly(false);
    return () => setOnBehalfOf(null);
  }, [profileId]);

  const group = useFormationGroupQuery(groupId);
  const participant = (group.data?.participants ?? []).find(
    (row) => row.enterprise_profile_id === profileId,
  );
  const profile = useProfile(profileId);
  const sectors = useSectors();
  const characteristics = useCharacteristics();
  const companySizes = useCompanySizes();

  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (profile.data && draft == null) {
      // The same field set `EnterpriseProfileForm` edits in the Django app —
      // its Meta.fields, minus the ones the form itself disables. A shorter
      // list here would mean an administrator can correct a participant's
      // sector in one app and their business type in neither.
      setDraft({
        // The name the participant typed, not the fabricated one in `name`.
        name: profile.data.display_name || profile.data.name,
        enterprise_type: profile.data.enterprise_type,
        address: profile.data.address,
        city: profile.data.city,
        zip_code: profile.data.zip_code,
        phone_number: profile.data.phone_number,
        website: profile.data.website ?? "",
        email: profile.data.email ?? "",
        company_size_id: profile.data.company_size_id ?? null,
        sector_ids: profile.data.sector_ids ?? [],
        characteristic_ids: profile.data.characteristic_ids ?? [],
      });
    }
  }, [profile.data, draft]);

  const selectedSectors = useMemo(
    () => new Set((draft?.sector_ids as string[]) ?? []),
    [draft],
  );
  const selectedCharacteristics = useMemo(
    () => new Set((draft?.characteristic_ids as string[]) ?? []),
    [draft],
  );

  if (!draft) return null;

  const set = (key: string, value: unknown) =>
    setDraft((current) => ({ ...(current ?? {}), [key]: value }));

  const toggleIn = (key: string, id: string) => {
    const current = new Set((draft[key] as string[]) ?? []);
    if (current.has(id)) current.delete(id);
    else current.add(id);
    set(key, [...current]);
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setErrors({});
    try {
      await patchProfile(profileId, draft as never);
      toast.success(
        t("formationGroups.toasts.profileUpdated", {
          participant: participant?.name ?? "",
        }),
      );
      navigate({ to: "/formation-groups/$id", params: { id: groupId } });
    } catch (error) {
      const detail = fieldErrors(error);
      if (detail) setErrors(detail);
      else toast.error(t("formationGroups.error"));
    } finally {
      setSaving(false);
    }
  };

  const field = (key: string, label: string) => (
    <div className="space-y-1">
      <Label htmlFor={`profile-${key}`} className="block">
        {label}
      </Label>
      {key === "address" ? (
        <AddressAutocomplete
          id={`profile-${key}`}
          value={(draft[key] as string) ?? ""}
          onChange={(value) => set(key, value)}
          onResolved={(result, suggestion) =>
            setDraft((current) => ({
              ...(current ?? {}),
              ...enterpriseAddressPatch(result, suggestion),
            }))
          }
          aria-invalid={Boolean(errors[key])}
        />
      ) : (
        <Input
          id={`profile-${key}`}
          value={(draft[key] as string) ?? ""}
          onChange={(event) => set(key, event.target.value)}
          aria-invalid={Boolean(errors[key])}
        />
      )}
      {errors[key] ? (
        <p role="alert" className="text-sm text-destructive">
          {errors[key]}
        </p>
      ) : null}
    </div>
  );

  return (
    <form onSubmit={save} className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <Button asChild variant="outline" size="sm">
          <Link to="/formation-groups/$id" params={{ id: groupId }}>
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("formationGroups.back")}
          </Link>
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          {t("formationGroups.participantProfileTitle")}
        </h1>
        {participant ? (
          <p className="text-sm text-muted-foreground">
            {t("formationGroups.detail.profileOf", {
              name: participant.name,
              email: participant.email,
            })}
          </p>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("questionnaire.steps.identification")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {field("name", t("questionnaire.identification.name"))}

          <div className="space-y-1">
            <Label htmlFor="profile-enterprise-type" className="block">
              {t("questionnaire.identification.enterpriseType")}
            </Label>
            <select
              id="profile-enterprise-type"
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground"
              value={(draft.enterprise_type as string) ?? ""}
              onChange={(event) => set("enterprise_type", event.target.value)}
            >
              {ENTERPRISE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`questionnaire.enterpriseTypes.${type}`)}
                </option>
              ))}
            </select>
            {errors.enterprise_type ? (
              <p role="alert" className="text-sm text-destructive">
                {errors.enterprise_type}
              </p>
            ) : null}
          </div>

          {field("address", t("questionnaire.identification.address"))}
          {field("city", t("questionnaire.identification.city"))}
          {field("zip_code", t("questionnaire.identification.zipCode"))}
          {field("phone_number", t("questionnaire.identification.phone"))}
          {field("website", t("questionnaire.identification.website"))}
          {field("email", t("questionnaire.identification.email"))}

          {/* Derived from the validated address, and read-only in the Django
              form too — shown rather than hidden so the administrator can see
              what the territory resolved to. */}
          <div className="space-y-1">
            <Label htmlFor="profile-province" className="block">
              {t("questionnaire.identification.province")}
            </Label>
            <Input
              id="profile-province"
              value={profile.data?.province ?? ""}
              readOnly
              className="bg-muted"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="profile-mrc" className="block">
              {t("questionnaire.identification.mrc")}
            </Label>
            <Input
              id="profile-mrc"
              value={profile.data?.mrc ?? ""}
              readOnly
              className="bg-muted"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="profile-region" className="block">
              {t("questionnaire.identification.region")}
            </Label>
            <Input
              id="profile-region"
              value={profile.data?.region ?? ""}
              readOnly
              className="bg-muted"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="profile-company-size" className="block">
              {t("questionnaire.identification.companySize")}
            </Label>
            <select
              id="profile-company-size"
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground"
              value={(draft.company_size_id as string) ?? ""}
              onChange={(event) => set("company_size_id", event.target.value)}
            >
              <option value="">
                {t("questionnaire.identification.selectPlaceholder")}
              </option>
              {(companySizes.data ?? []).map((size) => (
                <option key={size.id} value={size.id}>
                  {size.name}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("questionnaire.identification.sectors")}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1">
            {groupByCategory(sectors.data ?? [], t).map(
              ({ category, items }) => (
                <li key={category}>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground first:mt-0">
                    {category}
                  </p>
                  <ul className="mt-1 space-y-1">
                    {items.map((sector) => (
                      <li key={sector.id}>
                        <label className="flex items-start gap-2 text-sm text-foreground">
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 accent-primary"
                            checked={selectedSectors.has(sector.id)}
                            onChange={() => toggleIn("sector_ids", sector.id)}
                          />
                          <span>{sector.name}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </li>
              ),
            )}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {t("questionnaire.identification.characteristics")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1">
            {groupCharacteristics(characteristics.data ?? [], t).map(
              ({ group, items }) => (
                <li key={group}>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground first:mt-0">
                    {group}
                  </p>
                  <ul className="mt-1 space-y-1">
                    {items.map((characteristic) => (
                      <li key={characteristic.id}>
                        <label className="flex items-start gap-2 text-sm text-foreground">
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 accent-primary"
                            checked={selectedCharacteristics.has(
                              characteristic.id,
                            )}
                            onChange={() =>
                              toggleIn("characteristic_ids", characteristic.id)
                            }
                          />
                          <span>{characteristic.name}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </li>
              ),
            )}
          </ul>
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Button type="submit" disabled={saving}>
          {t("formationGroups.save")}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() =>
            navigate({ to: "/formation-groups/$id", params: { id: groupId } })
          }
        >
          {t("formationGroups.cancel")}
        </Button>
      </div>
    </form>
  );
}
